import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import useMutation from '@hooks/useGQLMutation'
import { ExportGroupSupportersDocument } from '@frontend/graphql/mutations/exportGroupSupporters.generated'
import { useAlertsStore } from '@stores/alertsStore'
import { toast } from '@stores/toastStore'
import { FormDialog } from '@components/molecules/FormDialog'
import { RadioGroupField } from '@components/molecules/RadioGroupField'
import { scopedTranslation } from '@utils/I18n'

import type { SupporterExportDialogState } from '../../types/dialogTypes'
import { buildFilters } from './exportFilters'

const t = scopedTranslation('components.supporter_export_dialog.dialog')

const ALERTS_PATH = 'supporterExport' as const

type ExportFormat = 'pdf' | 'csv'
type FormData = { format: ExportFormat }

const schema = yup
  .object({
    format: yup.mixed<ExportFormat>().oneOf(['pdf', 'csv']).required(),
  })
  .required()

type SupporterExportDialogProps = {
  onClose: () => void
  dialogState: SupporterExportDialogState
}

export function SupporterExportDialog({ onClose, dialogState }: SupporterExportDialogProps) {
  const { dialogData } = dialogState
  const { groupId, matchingCount, totalCount, sortBy, nameSearch, filters } = dialogData

  const { setAlerts, clearAlerts } = useAlertsStore(({ setAlerts, clearAlerts }) => ({
    setAlerts,
    clearAlerts,
  }))

  const { handleSubmit, setValue, watch } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { format: 'pdf' },
  })
  const format = watch('format')

  const isNarrowedByFilters = matchingCount < totalCount
  const scopeSummary = isNarrowedByFilters
    ? t('scope.filtered', { count: matchingCount, matching: matchingCount, total: totalCount })
    : t('scope.all_in_group', { count: totalCount })

  const exportMutation = useMutation(ExportGroupSupportersDocument)

  const onSubmit = ({ format: chosenFormat }: FormData) => {
    clearAlerts(ALERTS_PATH)

    exportMutation.mutate(
      {
        exportFormat: chosenFormat,
        groupId,
        sortBy,
        filters: buildFilters(filters, nameSearch),
      },
      {
        onSuccess: (response) => {
          if (response?.exportGroupSupporters?.success) {
            onClose()
            toast.success(t('alerts.success.title'), { description: t('alerts.success.body') })
            return
          }
          setAlerts({
            alertsPath: ALERTS_PATH,
            alerts:
              response?.exportGroupSupporters?.errors?.map((error: { message: string }) => ({
                type: 'error',
                details: { title: error.message },
              })) || [
                {
                  type: 'error',
                  details: { title: t('alerts.error.title'), description: t('alerts.error.body') },
                },
              ],
          })
        },
        onError: () => {
          setAlerts({
            alertsPath: ALERTS_PATH,
            alerts: [
              {
                type: 'error',
                details: { title: t('alerts.error.title'), description: t('alerts.error.body') },
              },
            ],
          })
        },
      }
    )
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      clearAlerts(ALERTS_PATH)
      onClose()
    }
  }

  return (
    <FormDialog.Root open={true} onOpenChange={handleOpenChange} testId="supporter-export-dialog">
      <FormDialog.Title alertsPath={ALERTS_PATH}>{t('title')}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit(onSubmit)}>
        <FormDialog.Body>
          <p>{scopeSummary}</p>
          <RadioGroupField<ExportFormat>
            label={t('form.format.label')}
            value={format}
            onValueChange={(value) => setValue('format', value)}
            options={[
              { value: 'pdf', label: t('form.format.pdf') },
              { value: 'csv', label: t('form.format.csv') },
            ]}
          />
        </FormDialog.Body>
        <FormDialog.Footer submitText={t('form.submit')} isLoading={exportMutation.isLoading} />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}
